import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import cls from 'clsx';
import { differenceInDays, format, parseISO, startOfDay } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { Left } from '@linktivity/link-icons';
import { Button, Dialog, Loading, message } from '@linktivity/link-ui';

import { RailwayOrderStatus } from '@link/china-railway/services/types';
import { useStore } from '@link/china-railway/stores';
import { getLanguageCode, REBOOK_STATUS } from '@link/china-railway/utils';

import styles from './orderDetail.module.css';

const OrderDetailView = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const { t, i18n } = useTranslation();
  const { bookingId: urlBookingId, token: urlToken } = useParams();
  const { chinaRailway } = useStore();
  const { bookingInfo } = chinaRailway;
  const orderInfo = bookingInfo.orderInfo;

  const bookingId = useMemo(
    () => urlBookingId || chinaRailway.bookingId,
    [urlBookingId, chinaRailway.bookingId]
  );
  const token = useMemo(
    () => urlToken || chinaRailway.token,
    [urlToken, chinaRailway.token]
  );

  // const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [isCancelOrderDialogVisible, setIsCancelOrderDialogVisible] =
    useState(false);

  // Fetch bookingInfo on page load
  useEffect(() => {
    if (bookingId && token) {
      setIsLoading(true);
      chinaRailway
        .getBookingData({
          bookingId,
          token,
          languageCode: getLanguageCode(i18n.language)
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [bookingId, token, chinaRailway, i18n.language]);

  // Check and update t_url from URL params
  useEffect(() => {
    const ticketUrl = searchParams.get('t_url');
    if (ticketUrl) {
      chinaRailway.setTicketUrl(decodeURIComponent(ticketUrl));
    }
  }, [searchParams, chinaRailway]);

  const { ticketUrl } = chinaRailway;

  const handleBack = useCallback(() => {
    if (ticketUrl) {
      location.assign(ticketUrl);
    } else {
      navigate(`/railway/${bookingId}/${token}?rebook=${REBOOK_STATUS}`, {
        replace: true
      });
    }
  }, [ticketUrl, navigate, bookingId, token]);

  const handleRebook = useCallback(() => {
    navigate(`/railway/${bookingId}/${token}?rebook=${REBOOK_STATUS}`);
  }, [navigate, bookingId, token]);

  // const toggleFAQ = useCallback((index: number) => {
  //   setExpandedFAQ(prev => (prev === index ? null : index));
  // }, []);

  // Format time (Date -> HH:mm)
  const formatTime = useCallback((date?: Date) => {
    if (!date) return '';
    return format(new Date(date), 'HH:mm');
  }, []);

  // Calculate trip duration
  const calculateDuration = useCallback(
    (departTime?: Date, arriveTime?: Date) => {
      if (!departTime || !arriveTime) return '';

      const departDate = new Date(departTime);
      const arriveDate = new Date(arriveTime);

      let totalMinutes = Math.floor(
        (arriveDate.getTime() - departDate.getTime()) / (1000 * 60)
      );
      if (totalMinutes < 0) {
        totalMinutes += 24 * 60; // Handle overnight trips
      }

      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      return t('views.trainList.durationFormat', { hours, minutes });
    },
    [t]
  );

  // Format departure datetime (YYYY年MM月DD日 HH:mm)
  const formatDateTime = useCallback(
    (date?: Date) => {
      if (!date) return '';
      const d = new Date(date);
      return t('views.orderDetail.dateTimeFormat', {
        year: d.getFullYear(),
        month: d.getMonth() + 1,
        day: d.getDate(),
        time: format(d, 'HH:mm')
      });
    },
    [t]
  );

  // Check if ticket is issued (show ticket info)
  const isIssued = useMemo(() => {
    return (
      bookingInfo.status === RailwayOrderStatus.Issued ||
      bookingInfo.status === RailwayOrderStatus.Used
    );
  }, [bookingInfo.status]);

  // Get all passengers' seat numbers
  const seatNumbers = useMemo(() => {
    if (!orderInfo?.passengers) return '';
    return orderInfo.passengers
      .map(p => p.seatNumber)
      .filter(Boolean)
      .join(', ');
  }, [orderInfo?.passengers]);

  const statusClass = useMemo(() => {
    switch (bookingInfo.status) {
      case RailwayOrderStatus.Issuing:
      case RailwayOrderStatus.Reserved:
      case RailwayOrderStatus.Booked:
      case RailwayOrderStatus.Issued:
      case RailwayOrderStatus.Used:
        return styles.statusTextCompleted;
      case RailwayOrderStatus.Cancelled:
      case RailwayOrderStatus.Cancelling:
        return styles.statusTextCancelled;
      case RailwayOrderStatus.Closed:
        return styles.statusTextClosed;
      default:
        return styles.statusTextCompleted;
    }
  }, [bookingInfo.status]);

  const isTicketSoldOut = useMemo(() => {
    return bookingInfo.status === RailwayOrderStatus.Closed;
  }, [bookingInfo.status]);

  const getSeatClassName = useCallback(
    (seatClass?: string) => {
      if (!seatClass) return '';
      return t(`seatClass.${seatClass}`);
    },
    [t]
  );

  // show loading status
  const isNotIssued = useMemo(() => {
    const loadingStatuses = [
      RailwayOrderStatus.Issuing,
      RailwayOrderStatus.Booked,
      RailwayOrderStatus.Reserved
    ] as RailwayOrderStatus[];
    return loadingStatuses.includes(bookingInfo.status as RailwayOrderStatus);
  }, [bookingInfo.status]);

  // Check if it's a pre-order status
  // Condition: departTime is within pre-order date range AND nowDate is before preOrderableDateFrom
  const isPreOrder = useMemo(() => {
    const departTime = orderInfo?.departTime;
    const nowDate = bookingInfo.nowDate;
    const preOrderableDateFrom = bookingInfo.planInfo?.preOrderableDateFrom;
    const preOrderableDateTo = bookingInfo.planInfo?.preOrderableDateTo;

    if (
      !departTime ||
      !nowDate ||
      !preOrderableDateFrom ||
      !preOrderableDateTo
    ) {
      return false;
    }

    // Normalize all dates to midnight (00:00:00) for pure date comparison
    const departDate = startOfDay(new Date(departTime));
    const nowDateParsed = startOfDay(parseISO(nowDate));
    const preFromParsed = startOfDay(parseISO(preOrderableDateFrom));
    const preToParsed = startOfDay(parseISO(preOrderableDateTo));

    // Check if departTime is within pre-order date range
    const isInPreOrderRange =
      departDate >= preFromParsed && departDate <= preToParsed;
    // Check if nowDate is before preOrderableDateFrom
    const isBeforePreOrderStart = nowDateParsed < preFromParsed;

    return isInPreOrderRange && isBeforePreOrderStart;
  }, [
    orderInfo?.departTime,
    bookingInfo.nowDate,
    bookingInfo.planInfo?.preOrderableDateFrom,
    bookingInfo.planInfo?.preOrderableDateTo
  ]);

  // Calculate days until sale starts
  const daysUntilSale = useMemo(() => {
    const nowDate = bookingInfo.nowDate;
    const preOrderableDateFrom = bookingInfo.planInfo?.preOrderableDateFrom;

    if (!nowDate || !preOrderableDateFrom) {
      return 0;
    }

    return differenceInDays(parseISO(preOrderableDateFrom), parseISO(nowDate));
  }, [bookingInfo.nowDate, bookingInfo.planInfo?.preOrderableDateFrom]);

  // Polling: request every 30 seconds when ticket is not issued
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (isNotIssued && bookingId && token) {
      pollingRef.current = setInterval(() => {
        chinaRailway.getBookingData({
          bookingId,
          token,
          languageCode: getLanguageCode(i18n.language)
        });
      }, 30000);
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [isNotIssued, bookingId, token, chinaRailway, i18n.language]);

  const showPreOrderTime = useMemo(() => {
    return isPreOrder && bookingInfo.planInfo?.preOrderableDateFrom;
  }, [isPreOrder, bookingInfo.planInfo?.preOrderableDateFrom]);

  // Format sale date (preOrderableDateFrom)
  const saleDateLabel = useMemo(() => {
    const preOrderableDateFrom = bookingInfo.planInfo?.preOrderableDateFrom;
    if (!preOrderableDateFrom) return '';
    const date = parseISO(preOrderableDateFrom);
    return t('views.orderDetail.saleDateFormat', {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate()
    });
  }, [bookingInfo.planInfo?.preOrderableDateFrom, t]);

  const departureTimeLabel = useMemo(() => {
    return formatDateTime(orderInfo?.departTime);
  }, [formatDateTime, orderInfo?.departTime]);

  // cancel not issued order
  const cancelOrder = useCallback(() => {
    chinaRailway
      .cancelOrder({ bookingId, token })
      .then(() => {
        message.success(t('views.orderDetail.cancelOrderSuccess'));
        chinaRailway.getBookingData({
          bookingId,
          token,
          languageCode: getLanguageCode(i18n.language)
        });
      })
      .catch(error => {
        console.error('cancelOrder error', error);
        message.error(t('views.orderDetail.cancelOrderFailed'));
      })
      .finally(() => {
        setIsCancelOrderDialogVisible(false);
      });
  }, [bookingId, chinaRailway, i18n.language, t, token]);

  return (
    <div className={styles.container}>
      {isLoading ? (
        <div className={styles.loading}>
          <Loading className={styles.loadingIcon} />
        </div>
      ) : (
        <>
          <div className={styles.header}>
            <button className={styles.backButton} onClick={handleBack}>
              <Left className={styles.backIcon} />
            </button>
            <h1 className={styles.headerTitle}>
              {t('views.orderDetail.title')}
            </h1>
          </div>

          <div className={styles.content}>
            {/* order detail card */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>
                {t('views.orderDetail.title')}
              </h2>

              <div className={styles.orderCard}>
                {/* status */}
                <div
                  className={cls(styles.statusRow, {
                    [styles.statusRowPreOrder]: isPreOrder
                  })}
                >
                  {isPreOrder ? (
                    <>
                      <div className={styles.preOrderStatusRow}>
                        <span className={styles.statusLoading}>
                          <Loading className={styles.statusLoadingIcon} />
                        </span>
                        <span
                          className={cls(
                            styles.statusText,
                            styles.statusTextCompleted
                          )}
                        >
                          {t('views.orderDetail.preOrderStatus', {
                            days: daysUntilSale
                          })}
                        </span>
                      </div>
                      <Button
                        className={styles.preOrderButton}
                        variant="outlined-danger"
                        onClick={() => setIsCancelOrderDialogVisible(true)}
                      >
                        {t('views.orderDetail.cancelOrder')}
                      </Button>
                    </>
                  ) : (
                    <>
                      {isNotIssued && (
                        <span className={styles.statusLoading}>
                          <Loading className={styles.statusLoadingIcon} />
                        </span>
                      )}
                      <span className={cls(styles.statusText, statusClass)}>
                        {t(`views.railway.status.${bookingInfo.status}`)}
                      </span>
                    </>
                  )}
                </div>

                {/* time information */}
                <div className={styles.trainInfo}>
                  <div className={styles.trainTimeSection}>
                    <span className={styles.trainTime}>
                      {formatTime(orderInfo?.departTime)}
                    </span>
                    <span className={styles.trainStation}>
                      {orderInfo?.fromStationName}
                    </span>
                  </div>

                  <div className={styles.trainMiddleSection}>
                    <span className={styles.trainDuration}>
                      {calculateDuration(
                        orderInfo?.departTime,
                        orderInfo?.arriveTime
                      )}
                    </span>
                    <div className={styles.trainLine}>
                      <span className={styles.trainLineBar} />
                      <span className={styles.trainLineArrow} />
                    </div>
                    <span className={styles.trainDuration}>
                      {orderInfo?.trainNumber}
                    </span>
                  </div>

                  <div className={styles.trainTimeSection}>
                    <span className={styles.trainTime}>
                      {formatTime(orderInfo?.arriveTime)}
                    </span>
                    <span className={styles.trainStation}>
                      {orderInfo?.toStationName}
                    </span>
                  </div>
                </div>

                {/* detailed information */}
                <div className={styles.detailInfo}>
                  <div className={styles.routeInfo}>
                    <span className={styles.routeText}>
                      {orderInfo?.fromStationName} → {orderInfo?.toStationName}
                    </span>
                    <span className={styles.ticketType}>
                      {t('views.orderDetail.highSpeedTicket')}
                    </span>
                  </div>

                  <div
                    className={cls({
                      [styles.infoRowsDisabled]: isTicketSoldOut
                    })}
                  >
                    {showPreOrderTime && (
                      <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>
                          {t('views.orderDetail.saleDate')}
                          {t('common.colon')}
                        </span>
                        <span className={styles.infoValuePrimary}>
                          {saleDateLabel}
                        </span>
                      </div>
                    )}

                    {departureTimeLabel && (
                      <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>
                          {t('views.orderDetail.departureTime')}
                          {t('common.colon')}
                        </span>
                        <span className={styles.infoValuePrimary}>
                          {departureTimeLabel}
                        </span>
                      </div>
                    )}
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>
                        {t('views.orderDetail.passengerName')}
                        {t('common.colon')}
                      </span>
                      <span className={styles.infoValue}>
                        {orderInfo?.passengers?.[0]?.name}
                      </span>
                    </div>

                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>
                        {t('views.orderDetail.ticketCount')}
                        {t('common.colon')}
                      </span>
                      <span className={styles.infoValue}>
                        {getSeatClassName(bookingInfo.planInfo?.seatClass)}
                        <span className={styles.countPrimary}>
                          ×{orderInfo?.passengers?.length || 0}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* order meta information */}
                <div className={styles.orderMeta}>
                  <div className={styles.metaRow}>
                    <span className={styles.metaLabel}>
                      {t('views.orderDetail.orderId')}
                      {t('common.colon')}
                    </span>
                    <span className={styles.metaValue}>
                      {orderInfo?.orderNo}
                    </span>
                  </div>
                  {bookingInfo.ticketIssuedAt && (
                    <div className={styles.metaRow}>
                      <span className={styles.metaLabel}>
                        {t('views.orderDetail.orderTime')}
                        {t('common.colon')}
                      </span>
                      <span className={styles.metaValue}>
                        {formatDateTime(bookingInfo.ticketIssuedAt)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* travel information - only shown when issued */}
            {isIssued && (
              <section className={styles.section}>
                <h2 className={styles.sectionTitle}>
                  {t('views.orderDetail.travelInfo')}
                </h2>

                <div className={styles.ticketCard}>
                  <div className={styles.ticketRow}>
                    <span className={styles.ticketLabel}>
                      {t('views.orderDetail.departureTime')}
                    </span>
                    <span className={styles.ticketValuePrimary}>
                      {formatDateTime(orderInfo?.departTime)}
                    </span>
                  </div>

                  <div className={styles.ticketRow}>
                    <span className={styles.ticketLabel}>
                      {t('views.orderDetail.trainNumber')}
                    </span>
                    <span className={styles.ticketValuePrimary}>
                      {orderInfo?.trainNumber}
                    </span>
                  </div>

                  <div className={styles.ticketRow}>
                    <span className={styles.ticketLabel}>
                      {t('views.orderDetail.seatNumber')}
                    </span>
                    <span className={styles.ticketValuePrimary}>
                      {seatNumbers}
                    </span>
                  </div>
                </div>
              </section>
            )}

            {/* frequently asked questions */}
            {/* <section className={styles.section}>
               <h2 className={styles.sectionTitle}>
                {t('views.orderDetail.faq')}
              </h2>

              <div className={styles.faqList}>
                {bookingInfo.qas?.map((faq: QA, index) => (
                  <div key={index} className={styles.faqItem}>
                    <button
                      className={styles.faqQuestion}
                      onClick={() => toggleFAQ(index)}
                      aria-expanded={expandedFAQ === index}
                    >
                      <span>{faq.question}</span>
                      <Down
                        className={cls(styles.faqIcon, {
                          [styles.faqIconExpanded]: expandedFAQ === index
                        })}
                      />
                    </button>
                    <div
                      className={cls(styles.faqAnswerWrapper, {
                        [styles.faqAnswerWrapperExpanded]: expandedFAQ === index
                      })}
                    >
                      <div className={styles.faqAnswerContent}>
                        <div className={styles.faqAnswer}>{faq.answer}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section> */}
          </div>

          {isTicketSoldOut && (
            <div className={styles.fixedBottom}>
              <Button className={styles.rebookButton} onClick={handleRebook}>
                {t('views.orderDetail.rebookTicket')}
              </Button>
            </div>
          )}
          <Dialog
            visible={isCancelOrderDialogVisible}
            title={t('views.orderDetail.cancelOrderDialog.title')}
            onOK={cancelOrder}
            onCancel={() => setIsCancelOrderDialogVisible(false)}
            onClose={() => setIsCancelOrderDialogVisible(false)}
            closable
            okText={t('views.orderDetail.cancelOrderDialog.confirm')}
            cancelText={t('views.orderDetail.cancelOrderDialog.cancel')}
          >
            <div className={styles.dialogContent}>
              {t('views.orderDetail.cancelOrderDialog.content')}
            </div>
          </Dialog>
        </>
      )}
    </div>
  );
};

const OrderDetail = observer(OrderDetailView);

export default OrderDetail;
